import { Box, Chip, Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography } from "@mui/material";
import { useCallback, useReducer, useState } from "react";
import { niceDate } from "../../../utils";
import EnhancedTable from "../../enhancedTable/EnhancedTable";
import { CommunicationService } from "../../../service/communicationService";
import useAbortController from "../../../hooks/useAbortController";
import { isLocalDev } from "../../../utils/environment";

// Message viewer component
function MessageViewerDialog({ message, onClose }) {
    if (!message) return null;

    return (
        <Dialog 
            open={!!message} 
            onClose={onClose}
            maxWidth="sm"
            fullWidth
        >
            <DialogTitle>Full Message</DialogTitle>
            <DialogContent>
                <Typography 
                    sx={{ 
                        whiteSpace: 'pre-wrap',
                        wordBreak: 'break-word',
                        mt: 2
                    }}
                >
                    {message}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

// Channel icons/labels
const channelLabels = {
    sms: { label: 'SMS', color: 'primary' },
    email: { label: 'Email', color: 'info' },
    phone: { label: 'Phone', color: 'success' },
    in_person: { label: 'In Person', color: 'default' },
    web_chat: { label: 'Web Chat', color: 'secondary' }
};

// Direction labels
const directionLabels = {
    inbound: { label: 'Inbound', color: 'success' },
    outbound: { label: 'Outbound', color: 'info' }
};

// Status labels
const statusLabels = {
    pending: { label: 'Pending', color: 'warning' },
    sent: { label: 'Sent', color: 'info' },
    delivered: { label: 'Delivered', color: 'success' },
    read: { label: 'Read', color: 'primary' },
    replied: { label: 'Replied', color: 'success' },
    failed: { label: 'Failed', color: 'error' }
};

// Communication type labels
const typeLabels = {
    general: 'General',
    appointment_reminder: 'Appointment Reminder',
    appointment_confirmation: 'Appointment Confirmation',
    order_ready: 'Order Ready',
    contact_lens_renewal: 'Contact Lens Renewal',
    prescription_expiring: 'Prescription Expiring',
    payment_reminder: 'Payment Reminder',
    marketing: 'Marketing',
    survey: 'Survey',
    recall: 'Recall',
    confirmation: 'Confirmation',
    follow_up: 'Follow Up'
};

// Create columns with message viewer callback
const getCommunicationColumns = (onMessageClick) => [
    {
        Header: 'Date',
        accessor: 'sentAt',
        enableFilter: true,
        type: 'date',
        Cell: ({ value }) => niceDate(value)
    },
    {
        Header: 'Direction',
        accessor: 'direction',
        enableFilter: true,
        type: 'enum',
        filterOptions: [
            { value: 'inbound', label: 'Inbound' },
            { value: 'outbound', label: 'Outbound' }
        ],
        Cell: ({ value }) => {
            const config = directionLabels[value] || { label: value, color: 'default' };
            return <Chip label={config.label} color={config.color} size="small" variant="outlined" />;
        }
    },
    {
        Header: 'Type',
        accessor: 'communicationType',
        enableFilter: true,
        type: 'enum',
        filterOptions: [
            { value: 'general', label: 'General' },
            { value: 'appointment_reminder', label: 'Appointment Reminder' },
            { value: 'appointment_confirmation', label: 'Appointment Confirmation' },
            { value: 'order_ready', label: 'Order Ready' },
            { value: 'contact_lens_renewal', label: 'Contact Lens Renewal' },
            { value: 'prescription_expiring', label: 'Prescription Expiring' },
            { value: 'payment_reminder', label: 'Payment Reminder' },
            { value: 'marketing', label: 'Marketing' },
            { value: 'survey', label: 'Survey' },
            { value: 'recall', label: 'Recall' },
            { value: 'confirmation', label: 'Confirmation' },
            { value: 'follow_up', label: 'Follow Up' }
        ],
        Cell: ({ value }) => typeLabels[value] || value
    },
    // {
    //     Header: 'Subject',
    //     accessor: 'subject',
    //     Cell: ({ value }) => value || '-'
    // },
    {
        Header: 'Message',
        accessor: 'messageBody',
        Cell: ({ value }) => {
            const maxLength = 80;
            if (!value) return '-';
            const truncated = value.length > maxLength;
            return (
                <Box
                    sx={{
                        cursor: truncated ? 'pointer' : 'default',
                        color: truncated ? 'primary.main' : 'inherit',
                        textDecoration: truncated ? 'underline' : 'none',
                        '&:hover': truncated ? {
                            textDecoration: 'underline',
                            opacity: 0.8
                        } : {}
                    }}
                    onClick={() => {
                        if (truncated) {
                            onMessageClick(value);
                        }
                    }}
                    role={truncated ? 'button' : undefined}
                    tabIndex={truncated ? 0 : undefined}
                    onKeyPress={(e) => {
                        if (truncated && (e.key === 'Enter' || e.key === ' ')) {
                            onMessageClick(value);
                        }
                    }}
                >
                    {truncated 
                        ? `${value.substring(0, maxLength)}...` 
                        : value
                    }
                </Box>
            );
        }
    },
    {
        Header: 'Channel',
        accessor: 'channel',
        enableFilter: true,
        type: 'enum',
        filterOptions: [
            { value: 'sms', label: 'SMS' },
            { value: 'email', label: 'Email' },
            { value: 'phone', label: 'Phone' },
            { value: 'in_person', label: 'In Person' },
            { value: 'web_chat', label: 'Web Chat' }
        ],
        Cell: ({ value }) => {
            const config = channelLabels[value] || { label: value, color: 'default' };
            return <Chip label={config.label} color={config.color} size="small" />;
        }
    },    
    {
        Header: 'Sender',
        accessor: 'senderName',
        Cell: ({ value }) => value || 'System'
    },
    {
        Header: 'Status',
        accessor: 'status',
        enableFilter: true,
        type: 'enum',
        filterOptions: [
            { value: 'pending', label: 'Pending' },
            { value: 'sent', label: 'Sent' },
            { value: 'delivered', label: 'Delivered' },
            { value: 'read', label: 'Read' },
            { value: 'replied', label: 'Replied' },
            { value: 'failed', label: 'Failed' }
        ],
        Cell: ({ value, row }) => {
            const config = statusLabels[value] || { label: value, color: 'default' };
            const errorMessage = row.original.errorMessage;
            return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Chip label={config.label} color={config.color} size="small" />
                    {errorMessage && value === 'failed' && (
                        <Typography 
                            variant="caption" 
                            color="error"
                            sx={{ 
                                fontStyle: 'italic',
                                maxWidth: 200,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                            title={errorMessage}
                        >
                            ({errorMessage})
                        </Typography>
                    )}
                </Box>
            );
        }
    },    
    // {
    //     Header: 'Provider',
    //     accessor: 'providerName',
    //     Cell: ({ value }) => value || '-'
    // }
];

export default function CommunicationTab({ customer }) {
    const [signal] = useAbortController();
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [state, updateState] = useReducer((state, params) => {
        return { ...state, ...params };
    }, {
        data: [],
        totalRowCount: 0,
        pageCount: 0,
        loading: false
    });

    // Get columns with message click handler
    const communicationColumns = getCommunicationColumns(setSelectedMessage);

    const fetchData = useCallback(({ pageSize, pageIndex, filters, sortBy }) => {
        updateState({ loading: true });

        // Build filter params
        const filterParams = {
            limit: pageSize,
            offset: pageSize * pageIndex
        };

        // Apply filters from EnhancedTable
        if (filters && filters.length > 0) {
            filters.forEach(filter => {
                if (filter.value) {
                    filterParams[filter.id] = filter.value;
                }
            });
        }

        CommunicationService.getCustomerCommunications(
            customer.customer.id,
            filterParams,
            signal
        )
            .then(result => {
                const { code, data } = result;
                if (code === 0) {
                    updateState({
                        data: data.rows || [],
                        totalRowCount: data.count || 0,
                        pageCount: Math.ceil((data.count || 0) / pageSize)
                    });
                }
                updateState({ loading: false });
            })
            .catch(error => {
                if (isLocalDev) {
                    console.error(error);
                }
                updateState({ loading: false, data: [], totalRowCount: 0, pageCount: 0 });
            });
    }, [customer.customer.id, signal]);

    return (
        <Box display="flex" flexDirection="column">
            <EnhancedTable
                columns={communicationColumns}
                data={state.data}
                totalRowCount={state.totalRowCount}
                pageCount={state.pageCount}
                fetchData={fetchData}
                loading={state.loading}
                selectionMode="none"
                enableAdd={false}
            />
            <MessageViewerDialog 
                message={selectedMessage} 
                onClose={() => setSelectedMessage(null)} 
            />
        </Box>
    );
}
